import { snakeCase } from 'lodash-es';
import { getStringError } from './getStringError';
import { getLogger } from './instance';
import { addBreadcrumb as addSentryBreadcrumb } from './sentry-client';

const EXCEPTION_LOG_SESSION_STORAGE_KEY = 'exception_logs';

/**
 * Logs unhandled API error responses to identify them so we can add the necessary error handling iteratively.
 *
 * Do not use for runtime exceptions where sending logs multiple times could happen due to multiple render cycles.
 * Instead, use `logExceptionOnce` for that.
 *
 * @param error - The error message or error object
 * @param data - Additional data to log. This should include `key` so we can easily trace the log back to a line of code
 *
 * @example
 * logUnknownErrorResponse('Error message', { key: 'universally.unique.period.separated.string', anyKey: value });
 */
export function logUnknownErrorResponse(
  error: unknown,
  data: Record<string, unknown> & { key: string }
) {
  const stringError = getStringError(error);

  if (process.env.PUBLIC_HR_ENV !== 'production') {
    // eslint-disable-next-line no-console -- allow console logging in non-prod envs for debugging
    console.error(stringError, data);
  }

  try {
    getLogger().log(
      'Unexpected error',
      {
        attention_required: true,
        error: stringError,
        data,
      },
      'error'
    );
  } catch {
    // eslint-disable-next-line no-console -- fallback when Datadog logger fails (e.g. `document` not defined in tests)
    console.error('Unexpected error', {
      attention_required: true,
      error: stringError,
      data,
    });
  }

  addSentryBreadcrumb({
    message: stringError,
    data,
    level: 'error',
  });
}

/**
 * Logs an exception once by checking if the error has already been logged in session storage.
 *
 * @param error - The error message or error object
 * @param logIdParams - Used for the session storage hash key. This should include `key` so we can easily trace the log back to a line of code. Do not include any large kv pairs in this object. KV pairs in this object are still logged to DD.
 * @param additionalData - Additional data to log to DD. Not used for the session storage log id. Useful for large messages or objects or unknown shapes.
 */
/**
 * Fallback dedupe ledger for when `sessionStorage` cannot be reached at all —
 * it does not exist during SSR, and a browser can refuse access (storage
 * disabled, some private-browsing modes, a third-party-blocked iframe).
 *
 * Deduping in memory narrows the scope from per-session to per-page-load, so at
 * worst an exception is logged again on the next page. That is the right way to
 * fail: skipping the log would lose the signal this function exists to capture,
 * and throwing would break the caller's own error handling — `observeSegmentLoad`
 * abandons its late-success revision if this throws on the way past.
 */
const inMemoryExceptionLogs = new Set<string>();

/**
 * The stored ledger, plus whether `sessionStorage` is usable at all.
 *
 * A present-but-malformed value is not the same as unreachable storage: the
 * ledger resets and keeps using `sessionStorage`, rather than dropping to the
 * in-memory fallback for the rest of the page.
 */
function readExceptionLogs(): { logs: string[]; isStorageAvailable: boolean } {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(EXCEPTION_LOG_SESSION_STORAGE_KEY);
  } catch {
    return { logs: [...inMemoryExceptionLogs], isStorageAvailable: false };
  }

  if (stored == null || stored === '') {
    return { logs: [], isStorageAvailable: true };
  }

  try {
    return { logs: JSON.parse(stored) as string[], isStorageAvailable: true };
  } catch {
    return { logs: [], isStorageAvailable: true };
  }
}

function writeExceptionLogs(logs: string[], isStorageAvailable: boolean) {
  if (!isStorageAvailable) {
    logs.forEach((logId) => inMemoryExceptionLogs.add(logId));
    return;
  }

  try {
    sessionStorage.setItem(
      EXCEPTION_LOG_SESSION_STORAGE_KEY,
      JSON.stringify(logs)
    );
  } catch {
    // Readable but not writable — a full quota, most often. Keep the ledger in
    // memory so the dedupe still holds for this page.
    logs.forEach((logId) => inMemoryExceptionLogs.add(logId));
  }
}

export function logExceptionOnce(
  error: unknown,
  logIdParams: Record<string, unknown> & { key: string },
  additionalData: Record<string, unknown> & { key?: never } = {}
) {
  const { logs: exceptionLogs, isStorageAvailable } = readExceptionLogs();

  const stringError = getStringError(error);
  const logIdParamsStringified = JSON.stringify(logIdParams);
  const logId = `${snakeCase(stringError)}-${logIdParamsStringified}`;

  if (exceptionLogs.includes(logId)) {
    return;
  }

  exceptionLogs.push(logId);

  writeExceptionLogs(exceptionLogs, isStorageAvailable);

  logUnknownErrorResponse(stringError, { ...logIdParams, ...additionalData });
}
